import { type ButtonBaseProps } from '@mui/material';
import StyledImagePreviewThumbnail from './StyledImagePreviewThumbnail';
import { type UploadedImage } from '@/lib/types';
import useImageAsDataUrl from '@/lib/utils/useImageAsDataUrl';
import ThumbnailLoadingPlaceholder from './ThumbnailLoadingPlaceholder';
import { VisibilityOff as ImageHiddenIcon } from '@mui/icons-material';
import useLoadImage from '../useLoadImage';
import ImagePreviewErrorState from './ImagePreviewErrorState';

type ImagePreviewThumbnailProps = Omit<
  ButtonBaseProps,
  'children' | 'tabIndex'
> & {
  image: UploadedImage;
  showInappropriateContentIndicators: boolean;
};

const ImagePreviewThumbnail = ({
  image,
  showInappropriateContentIndicators,
  ...props
}: ImagePreviewThumbnailProps) => {
  const [loading, thumbnailBlob, error] = useLoadImage({
    imageId: image.id,
    useThumbnail: true,
  });

  const thumbnailAsDataUrl = useImageAsDataUrl(
    image.file.thumbnailStorageFilename!,
    thumbnailBlob,
  );

  if (error !== null) {
    return <ImagePreviewErrorState />;
  }

  if (loading || !thumbnailAsDataUrl) {
    return <ThumbnailLoadingPlaceholder />;
  }

  return (
    <StyledImagePreviewThumbnail
      src={thumbnailAsDataUrl}
      containsInappropriateContent={showInappropriateContentIndicators}
      tabIndex={-1}
      {...props}
    >
      <ImageHiddenIcon />
    </StyledImagePreviewThumbnail>
  );
};

export default ImagePreviewThumbnail;
